import { Component, inject, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { GetFhirPatientSearchR4Response } from '../../../../shared/model/GetFhirPatientSearchR4Response';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { getLocaleFromUrl } from '../../../../shared/services/translate/locale-utils';
import { LocationCoordinates } from '../../../../shared/model/LocationCoordinates';
import { IdleTimeoutService } from '../../../../shared/services/idletimeout/idle-timeout.service';
import { MonitoringService } from '../../../../shared/services/monitoring/monitoring-service';
import { AuthService } from '../../../../shared/services/auth/auth.service';
import { GeolocationService } from '../../../../shared/services/geolocation/geolocation.service';
import { MCAppGatewayService } from '../../../../shared/services/mcappgateway/mcappgateway.service';
import { MatButtonModule } from "@angular/material/button";
import { PostGetProviderQuickSearchRequest } from '../../../../shared/model/providerlocationsearch/PostGetProviderQuickSearchRequest';
import { SearchStateService } from '../../../shared/services/search-state.service';
import { GetProviderSearchFilterResponse, ProviderSearchFilters } from '../../../../shared/model/providerlocationsearch/GetProviderSearchFilterResponse';
import { ProviderSearchComponent } from '../provider-search/provider-search.component';
import { ProviderSearchResultsComponent } from '../provider-search-results/provider-search-results.component';
import { ProviderSearchFilterComponent } from '../provider-search-filter/provider-search-filter.component';
import { LocationSearchComponent } from '../location-search/location-search.component';
import { LocationSearchResultsComponent } from '../location-search-results/location-search-results.component';
import { LocationSearchFilterComponent } from '../location-search-filter/location-search-filter.component';
import { LocationSearchDetailsComponent } from '../location-search-details/location-search-details.component';
import { SearchMockService } from '../../../shared/services/search-mock.service';
import { environment } from '../../../../../environments/environment';
import { ProviderSearchDetailsComponent } from '../provider-search-details/provider-search-details.component';
import { PostGetLocationQuickSearchRequest } from '../../../../shared/model/providerlocationsearch/PostGetLocationQuickSearchRequest';
import { GetLocationSearchFilterResponse, LocationSearchFilters } from '../../../../shared/model/providerlocationsearch/GetLocationSearchFilterResponse';
import { PostGetRegistryAffiliationRequest } from '../../../../shared/model/providerlocationsearch/PostGetRegistryAffiliationRequest';
import { PostGetPatientRegistryAffiliationRequest } from '../../../../shared/model/providerlocationsearch/PostGetPatientRegistryAffiliationRequest';

@Component({
  selector: 'app-provider-location-search-container-mobile',
  standalone: true,
  imports: [CommonModule, TranslateModule, MatButtonModule, ProviderSearchComponent, ProviderSearchResultsComponent, ProviderSearchFilterComponent, ProviderSearchDetailsComponent, LocationSearchComponent, LocationSearchResultsComponent, LocationSearchFilterComponent, LocationSearchDetailsComponent],
  templateUrl: './provider-location-search-container.component.html',
  styleUrl: './provider-location-search-container.component.css'
})
export class ProviderLocationSearchContainerComponent {
  geolocation = inject(GeolocationService);
  mcAppGateway = inject(MCAppGatewayService);
  auth: AuthService = inject(AuthService);
  searchState: SearchStateService = inject(SearchStateService);
  mockService: SearchMockService = inject(SearchMockService);

  @Input() isPlus: boolean | null = null;

  locale: string = '';
  language: string = '';
  currentLocation: LocationCoordinates | null = null;

  public title: string = 'Find a Provider';
  public providerBtnText: string = 'Provider';
  public locationBtnText: string = 'Location';
  public eptID: string = '';
  public wprID: string = '';
  public fhirID: string = '';
  public age?: number;
  public gender?: string;
  public patientInfo: GetFhirPatientSearchR4Response | null = null;
  public patientAffiliation: string | null = null;

  searchMode$ = this.searchState.searchMode$;
  showSpinner$ = this.searchState.showSpinner$;

  providerSearchResults$ = this.searchState.providerSearchResults$;
  providerSearchFilters$ = this.searchState.providerSearchFilters$;
  providerSearchFilterToggle$ = this.searchState.providerSearchFilterToggle$;
  currentProviderDetail$ = this.searchState.providerDetails$;
  currentProviderDetailsLocation$ = this.searchState.providerDetailsLocation$;

  locationSearchQuery$ = this.searchState.locationSearchQuery$;
  locationSearchResults$ = this.searchState.locationSearchResults$;
  locationSearchFilters$ = this.searchState.locationSearchFilters$;
  locationSearchFilterToggle$ = this.searchState.locationSearchFilterToggle$;
  currentLocationDetail$ = this.searchState.locationDetails$;
  currentLocationDetailsProvider$ = this.searchState.locationDetailsProvider$;

  public providerQuickSearchOptions: any[] = [];
  public locationQuickSearchOptions: any[] = [];
  public providerSearchFilters: ProviderSearchFilters | null = null;
  public locationSearchFilters: LocationSearchFilters | null = null
  public currentProviderDetails: any | null = null;
  public currentProviderDetailsLocation: any | null = null;
  public currentLocationDetails: any | null = null;
  public currentLocationDetailsProvider: any | null = null;
  public providerSearchTypeAheadOptions: string[] = []; // list of provider names
  public locationSearchTypeAheadOptions: string[] = []; // list of location types and services
  public cityZipTypeAheadOptions: string[] = []; // list of cities


  constructor(private translate: TranslateService, private idleTimeoutService: IdleTimeoutService, private monitoringService: MonitoringService) {
    this.locale = getLocaleFromUrl();
    sessionStorage.setItem("locale", this.locale);
    this.translate.use(this.locale);
  }

  async ngOnInit() {
    // Retrieve mobile-specific sessionStorage values
    this.eptID = sessionStorage.getItem("eptID") ?? "";
    this.wprID = sessionStorage.getItem("wprID") ?? "";
    this.fhirID = sessionStorage.getItem("fhirID") ?? "";
    this.age = sessionStorage.getItem("age") ? Number(sessionStorage.getItem("age")) : undefined;
    this.gender = sessionStorage.getItem("gender") || undefined;
    this.isPlus = sessionStorage.getItem("isPlus") === 'true';
    this.patientInfo = JSON.parse(sessionStorage.getItem("patientInfo") || '{}') as GetFhirPatientSearchR4Response;

    const persistedMode = localStorage.getItem("providerLocationSearchMode") as 'provider' | 'location' || 'provider';
    this.searchState.setSearchMode(persistedMode);

    // Watch for URL query param changes
    const urlParams = new URLSearchParams(window.location.search);
    const modeParam = urlParams.get('mode') as 'provider' | 'location' | null;
    if (modeParam === 'provider' || modeParam === 'location') {
      this.searchState.setSearchMode(modeParam);
    }

    
    if (this.locale === 'es-US') {
      this.language = 'spanish';
    }
    else {
      this.language = 'english';
    }

    this.translate.get('FIND_TITLE').subscribe((translated: string) => {
      this.title = translated;
    });

    this.translate.get('PROVIDER').subscribe((translated: string) => {
      this.providerBtnText = translated;
    });

    this.translate.get('LOCATION').subscribe((translated: string) => {
      this.locationBtnText = translated;
    });

    const locationPermissionAllowed = localStorage.getItem('locationPermissionAllowed');
    const storedLocation = localStorage.getItem('currentLocation');

    // For MyChart Plus App, we expect location to be passed in via pre-login page and stored in local storage,  
    // so we can attempt to use it immediately without prompting for geolocation on init. 

    // For standard mobile web, we will attempt geolocation on init 
    // but fallback to allowing user to trigger it if it fails or times out.
    if (locationPermissionAllowed === 'false') {
      // User has previously denied location permission, 
      // so we can skip trying to get location and just fetch data without it
      this.getData();
    }
    else if (locationPermissionAllowed === 'true' && storedLocation)  {
      try {
        this.currentLocation = JSON.parse(storedLocation) as LocationCoordinates;
      } catch (err) {
        console.error("Failed to parse stored location:", err);
        localStorage.removeItem('currentLocation'); // Clear invalid data
      }

      this.getData(); // Fetch care options immediately, using stored location if available
    }
    else {
      // For standard mobile, we can attempt to get location on init
      // Add a timeout to the geolocation request
      const geolocationPromise = this.geolocation.getCurrentLocationAsync();
      const timeoutPromise = new Promise<LocationCoordinates>((_, reject) =>
        setTimeout(() => reject(new Error('Geolocation timeout')), 2000) // 2 seconds timeout
      );

      await Promise.race([geolocationPromise, timeoutPromise])
        .then(location => {
            this.currentLocation = location;
        })
        .catch(err => {
          console.error("Geolocation failed:", err);
        })
        .finally(() => {
          this.getData();
        });
    }

    this.providerSearchFilters$.subscribe(filters => {
      this.providerSearchFilters = filters
    });

    this.currentProviderDetail$.subscribe(providerDetails => {
      this.currentProviderDetails = providerDetails;
    });

    this.currentProviderDetailsLocation$.subscribe(location => {
      this.currentProviderDetailsLocation = location;
    });

    this.locationSearchFilters$.subscribe(filters => {
      this.locationSearchFilters = filters
    });

    this.currentLocationDetail$.subscribe(locationDetails => {
      this.currentLocationDetails = locationDetails
    });

    this.currentLocationDetailsProvider$.subscribe(provider => {
      this.currentLocationDetailsProvider = provider
    });

    // Start watching for user inactivity
    this.idleTimeoutService.startWatching(true, this.isPlus);
  }

  getData() {
    // Reverse geocoding to get city from coordinates
    if (this.currentLocation) {
      this.mcAppGateway.PostGetAddressFromCoordinates(this.currentLocation).subscribe({
        next: (response) => {
          console.log('Reverse Geocoding Response:', response);
          this.searchState.setProviderSearchQuery({nameOrSpecialty: '', cityOrZip: response.city});
          this.searchState.setLocationSearchQuery({facilityOrLocationName: '', cityOrZip: response.city});
        },
        error: (error) => {
          console.error('Reverse Geocoding Error:', error);
        }
      });
    }

    this.mcAppGateway.GetCityTypeAheadOption().subscribe({
      next: (response) => {
        console.log('City Zip typeahead options response:', response);
        this.cityZipTypeAheadOptions= response;
      },
      error: (error) => {
        console.error('City Zip typeahead options Error:', error);
      }
    });

    this.mcAppGateway.GetProviderSearchTypeAheadOption().subscribe({
      next: (response) => {
        console.log('Provider TypeAhead Options Response:', response);
        this.providerSearchTypeAheadOptions = response;
      },
      error: (error) => {
        console.error('Provider TypeAhead Options Error:', error);
      }
    });

    // Get Provider Quick Search Options
    var provQuickSearchRequest = new PostGetProviderQuickSearchRequest();
    provQuickSearchRequest.language = this.locale;
    provQuickSearchRequest.age = this.age;
    provQuickSearchRequest.gender = this.gender;

    console.log("Provider Quick Search Request:", provQuickSearchRequest)

    this.mcAppGateway.PostGetProviderQuickSearch(provQuickSearchRequest).subscribe({
      next: (response) => {
        console.log('Provider Quick Search Response:', response);
        this.providerQuickSearchOptions = response;
      },
      error: (error) => {
        console.error('Provider Quick Search Error:', error);
      }
    });

    // Get location quick search options
    var locQuickSearchRequest = new PostGetLocationQuickSearchRequest();
    locQuickSearchRequest.language = this.locale;
    locQuickSearchRequest.age = this.age;
    locQuickSearchRequest.gender = this.gender;

    this.mcAppGateway.PostGetLocationQuickSearch(locQuickSearchRequest).subscribe({
      next: (response) => {
        console.log('Location Quick Search Response:', response);
        this.locationQuickSearchOptions = response;
      },
      error: (error) => {
        console.error('Location Quick Search Error:', error);
      }
    });

    // get insurance info if authenticated
    if(this.eptID != "" && this.eptID != null) {
      var getAffiliationRequest = new PostGetPatientRegistryAffiliationRequest();
      getAffiliationRequest.patientId = this.eptID
      

      this.mcAppGateway.PostGetPatientRegistryAffiliation(getAffiliationRequest).subscribe({
        next: (response) => {
          console.log('PostGetRegistryAffiliation API Response:', response);
          this.patientAffiliation = response.Affiliation ?? null;
        },
        error: (error) => {
          console.error('PostGetRegistryAffiliation API Error:', error);
        }
      });
    }

    // Get Provider Search Filters
    this.mcAppGateway.GetProviderSearchFilters().subscribe({
      next: (response: GetProviderSearchFilterResponse) => {
        console.log('Provider Search Filters API Response:', response);

        var filters = new ProviderSearchFilters(response);
        
        // Sort languages: English, Spanish, Korean, Mandarin, Vietnamese first, then rest alphabetically
        const priorityLanguages = ['English', 'Spanish', 'Korean', 'Mandarin', 'Vietnamese'];
        filters.languageList.sort((a, b) => {
          const aIndex = priorityLanguages.indexOf(a.value);
          const bIndex = priorityLanguages.indexOf(b.value);
          
          if (aIndex !== -1 && bIndex !== -1) {
            return aIndex - bIndex;
          }
          if (aIndex !== -1) return -1;
          if (bIndex !== -1) return 1;
          
          return a.value.localeCompare(b.value);
        });

        filters.specialtyList.forEach(specialty => {
          this.providerSearchTypeAheadOptions.push(specialty.value);
        });

        this.providerSearchTypeAheadOptions.sort((a, b) => a.localeCompare(b));

        this.searchState.setProviderSearchFilters(filters);
      },
      error: (error) => {
        console.error('Provider Search Filters Error:', error);
      }
    });

    this.mcAppGateway.GetLocationSearchFilters().subscribe({
      next: (response: GetLocationSearchFilterResponse) => {
        console.log('Location Search Filters API Response:', response);

        var filters = new LocationSearchFilters(response);
        this.searchState.setLocationSearchFilters(filters);

        // add location services and types
        // to locationSearchTypeAheadOptions
        filters.serviceList.forEach(service => {
          this.locationSearchTypeAheadOptions.push(service.value);
        });
        filters.typeList.forEach(type => {
          
          if (!this.locationSearchTypeAheadOptions.includes(type.value)) {
            this.locationSearchTypeAheadOptions.push(type.value);
          }
        });

        this.locationSearchTypeAheadOptions.sort((a, b) => a.localeCompare(b));
      },
      error: (error) => {
        console.error('Provider Search Filters Error:', error);
      }
    });
  }

  toggleSearchMode(mode: 'provider' | 'location') {
    this.searchState.setSearchMode(mode);
  }
}
